import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SURVEY_ACCESS_HOURS, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { UserPlus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CredentialsCard, type SurveyCredentials } from "./-components/credentials-card";
import { EndAccessDialog } from "./-components/end-access-dialog";
import { GrantCard, type SurveyGrant } from "./-components/grant-card";
import { GrantDialog } from "./-components/grant-dialog";
import { ResidentsDialog } from "./-components/residents-dialog";

export const Route = createFileRoute("/_authenticated/_care/survey-access/")({
  beforeLoad: ({ context }) => guardRoute(context.me, "survey-access"),
  component: SurveyAccessPage,
});

/** Let each surveyor in to the residents they ask for, see who is in and for how long, end it
 * early. */
function SurveyAccessPage() {
  const { facilityId } = useFacility();
  const building = facilityId === "all" ? null : facilityId;
  const queryClient = useQueryClient();
  const canMint = useCan("staff:create", building);
  const canExport = useCan("resident:export", building);
  const canGrant = canMint && canExport;
  const [granting, setGranting] = useState(false);
  const [credentials, setCredentials] = useState<SurveyCredentials | null>(null);
  const [changing, setChanging] = useState<SurveyGrant | null>(null);
  const [ending, setEnding] = useState<SurveyGrant | null>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const list = useQuery(
    orpc.surveyPortal.access.list.queryOptions({ input: { facilityId: building ?? undefined } }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
    enabled: canGrant,
  });
  const end = useMutation(
    orpc.surveyPortal.access.revoke.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.surveyPortal.access.key() });
        toast.success("Survey access ended");
        setEnding(null);
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const rows = list.data ?? [];
  const live = rows.filter((row) => row.state === "active");
  const choices = (facilities.data ?? []).filter((row) => !building || row.id === building);

  return (
    <>
      <PageHeader
        title="Survey access"
        meta={`Read-only chart access for state surveyors. Each grant ends on its own ${SURVEY_ACCESS_HOURS} hours after it is given.`}
        actions={
          canGrant ? (
            <Button disabled={choices.length === 0} onClick={() => setGranting(true)}>
              <UserPlus className="size-4" />
              Grant survey access
            </Button>
          ) : null
        }
      />

      {credentials ? (
        <CredentialsCard credentials={credentials} onDismiss={() => setCredentials(null)} />
      ) : null}

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : list.isError ? (
        <ReadFailed
          message="Could not load survey access."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No surveyors have access"
          description="When a survey team arrives, grant each surveyor access to the residents they ask for."
        />
      ) : (
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            {live.length} active, {rows.length - live.length} ended in the last 30 days
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            {rows.map((grant) => (
              <GrantCard
                key={grant.id}
                canChange={canGrant}
                canEnd={canExport}
                grant={grant}
                now={now}
                onChangeResidents={() => setChanging(grant)}
                onEnd={() => setEnding(grant)}
              />
            ))}
          </div>
        </div>
      )}

      {granting ? (
        <GrantDialog
          facilities={choices}
          facilityId={building}
          open
          onGranted={setCredentials}
          onOpenChange={setGranting}
        />
      ) : null}
      {changing ? <ResidentsDialog grant={changing} onClose={() => setChanging(null)} /> : null}
      <EndAccessDialog
        name={ending?.surveyor.name ?? null}
        pending={end.isPending}
        onCancel={() => setEnding(null)}
        onConfirm={() => ending && end.mutate({ grantId: ending.id })}
      />
    </>
  );
}
